import { useEffect, useMemo, useState } from 'react';
import { BlockStack } from '@shopify/polaris';
import { getRequest } from '../../../../shared/components/functions';
import PeriodBreakdownTable from './PeriodBreakdownTable';
import YearMonthMatrix from './YearMonthMatrix';
import TopPartnersCard from './TopPartnersCard';
import CategoryBreakdownCard from './CategoryBreakdownCard';
import BusinessUnitCard from './BusinessUnitCard';
import BusinessUnitFilter, { businessUnitParam, useBusinessUnitFilter, useBusinessUnitName } from './BusinessUnitFilter';
import PeriodDrilldownModal from './PeriodDrilldownModal';
import { getYears, toNumber } from './analyticsHelpers';

const costColumns = [
  { key: 'invoice_count', label: 'Invoices', format: 'integer' },
  { key: 'total_excl', label: 'Booked (excl. VAT)', format: 'currency' },
  { key: 'credited_excl', label: 'Credit notes (excl. VAT)', format: 'currency' },
  { key: 'net_excl', label: 'Net costs (excl. VAT)', format: 'currency' },
  { key: 'total_incl', label: 'Booked (incl. VAT)', format: 'currency' },
  { key: 'open_amount', label: 'Still open', format: 'currency' }
];

const baseDrilldownConfig = {
  titlePrefix: 'Costs',
  endpoint: '/api/metrics/costs-period-detail',
  entityLabel: 'suppliers',
  entityHeading: 'Supplier',
  invoiceLinkBase: '/invoices_in'
};

export default function CostsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillPeriod, setDrillPeriod] = useState(null);
  const [businessUnitId, setBusinessUnitId] = useBusinessUnitFilter();
  const unitName = useBusinessUnitName(businessUnitId);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`/api/metrics/costs-table${businessUnitParam(businessUnitId, true)}`);
      if (cancelled) return;
      const rows = Array.isArray(result) ? result : [];
      setData(
        rows.map((row) => ({
          ...row,
          net_excl: toNumber(row.total_excl) - toNumber(row.credited_excl)
        }))
      );
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const years = useMemo(() => getYears(data), [data]);
  const titleSuffix = unitName ? ` — ${unitName}` : '';
  const drilldownConfig = useMemo(() => ({ ...baseDrilldownConfig, titlePrefix: `Costs${titleSuffix}` }), [titleSuffix]);

  return (
    <BlockStack gap="400">
      <BusinessUnitFilter value={businessUnitId} onChange={setBusinessUnitId} />
      <PeriodBreakdownTable
        title={`Incoming invoices (costs)${titleSuffix}`}
        subtitle="Supplier invoicing per period, based on the booked date. Excl. VAT uses the amount stored on each incoming invoice and credit note (exact for Peppol documents, equal to the total for VAT-free ones). Net costs = booked minus incoming credit notes. Click a period to see where the money went."
        rows={data}
        columns={costColumns}
        netKey="net_excl"
        netLabel="net costs"
        invertDelta
        loading={loading}
        onPeriodClick={setDrillPeriod}
      />
      <CategoryBreakdownCard businessUnitId={businessUnitId} />
      <YearMonthMatrix
        title={`Costs heatmap${titleSuffix}`}
        subtitle="Net costs (excl. VAT) across all years — darker is more spend. Switch the view for coarser periods, or turn on cumulative to see the running year total per period."
        rows={data}
        valueKey="net_excl"
        colorRgb="178, 34, 34"
        loading={loading}
        onPeriodClick={setDrillPeriod}
      />
      {!businessUnitId && (
        <BusinessUnitCard
          title="Costs per business unit"
          subtitle="Incoming invoice totals (incl. VAT) split per business unit. Business units are tagged on incoming invoices since 2025, so earlier years are not shown."
          endpoint="/api/metrics/costs-by-business-unit"
        />
      )}
      {years.length > 0 && (
        <TopPartnersCard
          title={`Top suppliers${titleSuffix}`}
          subtitle="Where your money goes, incl. concentration on single suppliers. Values incl. VAT, based on booked date."
          endpoint="/api/metrics/top-suppliers"
          idKey="supplier_id"
          nameKey="supplier_name"
          entityLabel="suppliers"
          years={years}
          invertDelta
          businessUnitId={businessUnitId}
        />
      )}
      <PeriodDrilldownModal period={drillPeriod} onClose={() => setDrillPeriod(null)} config={drilldownConfig} businessUnitId={businessUnitId} />
    </BlockStack>
  );
}
